<script setup lang="ts">
import ClientOverviewActivitySection from '@/components/clients/overview/ClientOverviewActivitySection.vue';
import ClientOverviewAttentionSection from '@/components/clients/overview/ClientOverviewAttentionSection.vue';
import ClientOverviewConversationSection from '@/components/clients/overview/ClientOverviewConversationSection.vue';
import ClientOverviewNotesSection from '@/components/clients/overview/ClientOverviewNotesSection.vue';
import ClientOverviewProjectsSection from '@/components/clients/overview/ClientOverviewProjectsSection.vue';
import MetricCard from '@/components/workspace/MetricCard.vue';
import type {
    ClientOverviewActivityItem,
    ClientOverviewAttentionCard,
    ClientOverviewChannel,
    ClientOverviewConversationItem,
    ClientOverviewHealthIndicator,
    ClientOverviewProjectCard,
} from './types';

interface Props {
    canMutate: boolean;
    overviewAttentionCards: ClientOverviewAttentionCard[];
    overviewBriefing?: string;
    overviewActiveProjects: ClientOverviewProjectCard[];
    notes: string;
    notesOpen: boolean;
    notesSaving: boolean;
    overviewConversationItems: ClientOverviewConversationItem[];
    overviewConversationSummary: string;
    overviewHealthIndicators: ClientOverviewHealthIndicator[];
    overviewActivityItems: ClientOverviewActivityItem[];
    projectCount: number;
    openTaskCount: number;
    pendingApprovalCount: number;
    outstandingInvoiceCount: number;
    communicationChannelIcon: (channel: ClientOverviewChannel) => any;
    communicationChannelLabel: (channel: ClientOverviewChannel) => string;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: 'open-attention-card', cardId: string): void,
    (e: 'create-project'): void,
    (e: 'open-work'): void,
    (e: 'update:notes', value: string): void,
    (e: 'update:notesOpen', value: boolean): void,
    (e: 'save-notes'): void,
    (e: 'open-comms'): void,
    (e: 'open-activity'): void,
    (e: 'open-approvals'): void,
    (e: 'open-finance'): void,
}>();
</script>

<template>
    <div class="mt-0 space-y-8">
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard interactive density="compact" label="Projects" :value="projectCount" description="Work connected to this client" @activate="emit('open-work')" />
            <MetricCard interactive density="compact" label="Open tasks" :value="openTaskCount" description="Tasks still moving through delivery" @activate="emit('open-work')" />
            <MetricCard interactive density="compact" label="Pending approvals" :value="pendingApprovalCount" description="Waiting on sign-off or response" @activate="emit('open-approvals')" />
            <MetricCard interactive density="compact" label="Outstanding invoices" :value="outstandingInvoiceCount" description="Invoices still awaiting payment" @activate="emit('open-finance')" />
        </div>

        <ClientOverviewAttentionSection
            :cards="overviewAttentionCards"
            :briefing="overviewBriefing"
            @open-card="emit('open-attention-card', $event)"
        />

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(340px,0.85fr)] xl:gap-8">
            <div class="space-y-6">
                <ClientOverviewProjectsSection
                    :projects="overviewActiveProjects"
                    :can-mutate="canMutate"
                    @create-project="emit('create-project')"
                    @open-work="emit('open-work')"
                />

                <ClientOverviewNotesSection
                    :model-value="notes"
                    :open="notesOpen"
                    :can-mutate="canMutate"
                    :saving="notesSaving"
                    @update:model-value="emit('update:notes', $event)"
                    @update:open="emit('update:notesOpen', $event)"
                    @save="emit('save-notes')"
                />
            </div>

            <div class="space-y-6">
                <ClientOverviewConversationSection
                    :items="overviewConversationItems"
                    :summary="overviewConversationSummary"
                    :channel-icon="communicationChannelIcon"
                    :channel-label="communicationChannelLabel"
                    @open-comms="emit('open-comms')"
                />

                <ClientOverviewActivitySection
                    :items="overviewActivityItems"
                    @open-activity="emit('open-activity')"
                />
            </div>
        </div>
    </div>
</template>
